import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { isSupabaseConfigured, supabase } from "../lib/supabaseClient";
import { requireEventbriteEnabled } from "../eventbrite/eventbriteFeature";

const AuthContext = createContext(undefined);

const configurationMessage =
  "Supabase authentication is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to your environment.";

const requireSupabase = () => {
  if (!supabase) {
    throw new Error(configurationMessage);
  }

  return supabase;
};

export const AuthProvider = ({ children, disabled = false }) => {
  const isDisabled = disabled || window.location.pathname === "/demo";
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [isRoleLoading, setIsRoleLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(isSupabaseConfigured && !isDisabled);
  const [authError, setAuthError] = useState(
    isSupabaseConfigured || isDisabled ? null : configurationMessage
  );

  useEffect(() => {
    if (isDisabled || !supabase) {
      setIsLoading(false);
      return undefined;
    }

    let isMounted = true;

    supabase.auth.getSession().then(({ data, error }) => {
      if (!isMounted) return;

      if (error) {
        setAuthError(error.message);
      }

      setSession(data.session);
      setUser(data.session?.user ?? null);
      setIsLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      setAuthError(null);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [isDisabled]);

  useEffect(() => {
    if (!user?.id || !supabase) {
      setRole(null);
      setIsRoleLoading(false);
      return undefined;
    }

    let isMounted = true;
    setIsRoleLoading(true);
    supabase
      .rpc("current_user_role")
      .then(({ data, error }) => {
        if (!isMounted) return;
        if (error) {
          setAuthError(error.message);
          setRole("user");
        } else {
          setRole(data || "user");
        }
        setIsRoleLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  const value = useMemo(
    () => ({
      authError,
      isAuthenticated: Boolean(session),
      isConfigured: isSupabaseConfigured || isDisabled,
      isLoading: isLoading || isRoleLoading || Boolean(user && role === null),
      isAdmin: role === "admin",
      role,
      session,
      user,
      clearAuthError: () => setAuthError(null),
      signIn: async ({ email, password }) => {
        const client = requireSupabase();
        const { error } = await client.auth.signInWithPassword({ email, password });
        if (error) throw error;
      },
      signInWithGoogle: async () => {
        const client = requireSupabase();
        const { error } = await client.auth.signInWithOAuth({
          provider: "google",
          options: {
            redirectTo: `${window.location.origin}/auth/callback`,
          },
        });
        if (error) throw error;
      },
      signInWithEventbrite: async () => {
        requireEventbriteEnabled();
        const client = requireSupabase();
        const { error } = await client.auth.signInWithOAuth({
          provider: "custom:eventbrite",
          options: { redirectTo: `${window.location.origin}/auth/callback?provider=eventbrite` },
        });
        if (error) throw error;
      },
      signUp: async ({ email, password }) => {
        const client = requireSupabase();
        const { error } = await client.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback`,
          },
        });
        if (error) throw error;
      },
      signOut: async () => {
        const client = requireSupabase();
        const { error } = await client.auth.signOut();
        if (error) throw error;
      },
      sendPasswordReset: async (email) => {
        const client = requireSupabase();
        const { error } = await client.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
      },
      updatePassword: async (password) => {
        const client = requireSupabase();
        const { error } = await client.auth.updateUser({ password });
        if (error) throw error;
      },
    }),
    [authError, isDisabled, isLoading, isRoleLoading, role, session, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside an AuthProvider");
  }

  return context;
};

